import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { IconButton } from '../../shared/button/IconButton';
import { Icon } from '../../shared/icon/Icon';
import { Search, Delete, Settings, CaretSide } from '../../shared/icons';
import { Position } from '../../shared/modal/PositionModal';
import styles from './DocumentViewer.module.scss';
import { PdfSearchModal } from './PDFSearchModal';
import { getCurrentPdfPage, getSelectedSearchResult, getPdfSearchTerm, getSearchResults, setPdfSearchTerm, setSelectedSearchResult, getCanPreviousHighlight, clearPdfSearchTerm, PDFSearchType, getSearchSettings, getIndexOfCurrentlySelected, cancelSearchSettings, updateSearchSettings, applySearchSettings, getCanApplyChanges, PdfSearchResults, getCanNextHighlight, setScrollToInitialSearchResults, getScrollToInitialSearchResults, getPdfText } from './store';

const { white } = styles;

const SEARCH_BAR_ICONS = 44;

interface PDFSearchInputProps {
    scrollToPage: (pageNumber: number) => void;
    showScheduleStartPage: boolean;
    isSupportDocument: boolean;
}

export const PDFSearchInput: React.FC<PDFSearchInputProps> = ({
    scrollToPage,
    showScheduleStartPage,
    isSupportDocument
}) => {
    const [position, setPosition] = useState<Position | null>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const currentPage = useAppSelector(getCurrentPdfPage);
    const pdfSearchTerm = useAppSelector(getPdfSearchTerm);
    const searchResults = useAppSelector(getSearchResults);
    const selectedSearchResult = useAppSelector(getSelectedSearchResult);
    const canApplyChanges = useAppSelector(getCanApplyChanges);
    const { searchType, scheduleOnly } = useAppSelector(getSearchSettings);
    const indexOfCurrentlySelected = useAppSelector(getIndexOfCurrentlySelected);
    const canNextHighlight = useAppSelector(getCanNextHighlight);
    const canPreviousHighlight = useAppSelector(getCanPreviousHighlight);
    const shouldScrollToInitialSearchResult = useAppSelector(getScrollToInitialSearchResults);
    const pdfText = useAppSelector(getPdfText);

    const isNonSearchablePdf = useMemo(() => pdfText.every(({ textItems }) => textItems.length === 0), [pdfText]);
    const searchDisabled = useMemo(() => pdfText.length === 0 || isNonSearchablePdf, [pdfText, isNonSearchablePdf]);
    const showSearchDisableMessage = useMemo(() => pdfText.length > 0 && isNonSearchablePdf, [pdfText, isNonSearchablePdf]);
    const searchPlaceholder = useMemo(() => searchDisabled ? '' : 'Search...', [searchDisabled]);

    const updateSearchTerm = useCallback((value: string) => dispatch(setPdfSearchTerm(value)), [dispatch]);

    const scrollToHighlightedAnswer = useCallback((page: number, highlightIndex: number) => {
        const value = document.getElementById(`search-highlight-ref-${page}-${highlightIndex}`);
        if (!isNull(value)) {
            value.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, []);

    const moveHighlightIndex = useCallback((selected: PdfSearchResults) => {
        dispatch(setSelectedSearchResult(selected));
        if (selected.page !== currentPage) {
            scrollToPage(selected.page);
            setTimeout(() => scrollToHighlightedAnswer(selected.page, selected.indexes[0]), 1500);
        } else {
            scrollToHighlightedAnswer(selected.page, selected.indexes[0]);
        }
    }, [dispatch, currentPage, scrollToHighlightedAnswer, scrollToPage]);

    const nextHighlight = useCallback(() => {
        if (!isNull(indexOfCurrentlySelected) && indexOfCurrentlySelected + 1 <= (searchResults.length - 1)) {
            const nextSelected = searchResults[indexOfCurrentlySelected + 1];
            moveHighlightIndex(nextSelected);
        }
    }, [searchResults, moveHighlightIndex, indexOfCurrentlySelected]);

    const previousHighlight = useCallback(() => {
        if (!isNull(indexOfCurrentlySelected) && indexOfCurrentlySelected - 1 >= 0) {
            const previousSelected = searchResults[indexOfCurrentlySelected - 1];
            moveHighlightIndex(previousSelected);
        }
    }, [searchResults, moveHighlightIndex, indexOfCurrentlySelected]);

    const clearSearch = useCallback(() => dispatch(clearPdfSearchTerm()), [dispatch]);

    const openSearchSettings = useCallback((x: number, y: number) => {
        setPosition({ x, y });
        setModalOpen(true);
    }, []);

    const closeSearchSettings = useCallback(() => {
        setPosition(null);
        setModalOpen(false);
        dispatch(cancelSearchSettings());
    }, [dispatch]);

    const changeSearchType = useCallback((type: PDFSearchType) => dispatch(updateSearchSettings('searchType', type)), [dispatch]);

    const toggleScheduleOnly = useCallback(() => dispatch(updateSearchSettings('scheduleOnly', !scheduleOnly)), [dispatch, scheduleOnly]);

    const searchResultsWidth = useMemo(() => searchResults.length >= 1000 ? 85 : 55, [searchResults]);
    const searchInputWidth = useMemo(() => `calc(100% - ${(searchResultsWidth + SEARCH_BAR_ICONS)}px)`, [searchResultsWidth]);
    const searchResultsIndex = useMemo(() => pdfSearchTerm !== '' && searchResults.length > 0 && `${!isNull(indexOfCurrentlySelected) ? indexOfCurrentlySelected + 1 : '-'} / ${searchResults.length}` || '', [pdfSearchTerm, indexOfCurrentlySelected, searchResults]);

    const searchWrapperWidth = useMemo(() => {
        if (isSupportDocument) {
            return 190;
        }
        if (!showScheduleStartPage) {
            return 150;
        }
        return 170;
    }, [isSupportDocument, showScheduleStartPage]);

    const searchInput = useMemo(() => {
        if (showSearchDisableMessage) {
            return <div className={styles.searchPdfInputDisabled}>This document cannot be searched</div>;
        }
        return <input
            placeholder={searchPlaceholder}
            value={pdfSearchTerm}
            onChange={e => updateSearchTerm(e.target.value)}
            className={styles.searchPdfInput}
            style={{ width: searchInputWidth }}
        />;
    }, [pdfSearchTerm, searchInputWidth, searchPlaceholder, showSearchDisableMessage, updateSearchTerm]);

    const applySettings = useCallback(() => {
        setPosition(null);
        setModalOpen(false);
        dispatch(applySearchSettings());
    }, [dispatch]);

    useEffect(() => {
        if (!isNull(selectedSearchResult) && shouldScrollToInitialSearchResult) {
            scrollToPage(selectedSearchResult.page);
            scrollToHighlightedAnswer(selectedSearchResult.page, selectedSearchResult.indexes[0]);
            dispatch(setScrollToInitialSearchResults(false));
        }
    }, [selectedSearchResult, scrollToPage, scrollToHighlightedAnswer, shouldScrollToInitialSearchResult, dispatch]);

    return (
        <>
            <div className={styles.searchWrapper} style={{ width: `calc(100% - ${searchWrapperWidth}px)` }}>
                <div className={styles.inputWrapper}>
                    <div className={styles.searchIcon}>
                        <Icon fontSize={20} icon={Search} fill={white} />
                    </div>
                    {searchInput}
                    <div className={styles.searchResultsLength} style={{ width: searchResultsWidth }}>{searchResultsIndex}</div>
                    <div className={styles.searchIcon}>
                        {pdfSearchTerm !== '' && <IconButton onClick={clearSearch} fontSize={20} icon={Delete} />}
                    </div>
                </div>
                <div className={styles.resultsToggleWrapper}>
                    {searchResults.length > 1 &&
                        <>
                            <div style={{ transform: 'rotateY(180deg)' }}>
                                <IconButton onClick={previousHighlight} disabled={!canPreviousHighlight} fontSize={12} icon={CaretSide} />
                            </div>
                            <IconButton onClick={nextHighlight} disabled={!canNextHighlight} fontSize={12} icon={CaretSide} />
                        </>
                    }
                </div>
                <div className={styles.searchSettingsIcon}>
                    <IconButton onClick={e => openSearchSettings(e.clientX, e.clientY)} fontSize={24} icon={Settings} disabled={searchDisabled} />
                </div>
            </div>
            <PdfSearchModal
                isOpen={modalOpen}
                closeModal={closeSearchSettings}
                testId='pdf-search'
                position={position}
                toggleScheduleOnly={toggleScheduleOnly}
                applyChanges={applySettings}
                changeSearchType={changeSearchType}
                searchType={searchType}
                scheduleOnly={scheduleOnly}
                showScheduleStartPage={showScheduleStartPage}
                canApplyChanges={canApplyChanges}
            />
        </>
    );
};
